import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';

import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Set Portuguese Brazil as default locale
dayjs.locale('pt-br');

import { signupWithEmailAndPassword } from 'services/firebase/auth';
import { UpdateHouse2Firebase } from 'services/firebase/update';
import { auth } from 'services/firebase/client';
// import { createCustomer } from 'utils/data/pagarme';
import { IUser } from 'utils/types/User';
import { validateCPF } from 'utils/validation';
import { useNavigate, useLocation } from 'react-router-dom';
import ErrorModal from 'components/ErrorModal';

const SignUp = () => {
  const location = useLocation();
  // const currentYear = new Date().getFullYear();

  // State for form data

  const [formData, setFormData] = useState<IUser>({
    id: '',
    email: '',
    cpf: '',
    fullName: '',
    bornDate: new Date(),
    gender: '',
    phoneNumber: '',
    cep: '',
    address: {
      street: '',
      neighborhood: '',
      city: '',
      zip: '',
      number: '',
      complement: '',
      reference: '',
      recipient: '',
      uf: '',
    },
    receiveUpdates: true,
  });

  const navigate = useNavigate();

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [birthDate, setBirthDate] = useState<dayjs.Dayjs | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleBirthDateChange = (newDate: dayjs.Dayjs | null) => {
    setBirthDate(newDate);
  };

  // Handle input changes
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      if (name === 'phoneNumber') {
        // Limit phone number to 9 digits only
        const numericValue = value.replace(/\D/g, '').slice(0, 9);
        setPhoneNumber(numericValue);
        return;
      } else if (name === 'areaCode') {
        // Limit DDD to 2 digits only
        const numericValue = value.replace(/\D/g, '').slice(0, 2);
        setAreaCode(numericValue);
        return;
      } else if (name === 'password') {
        setPassword(value);
        return;
      } else if (name === 'confirmPassword') {
        setConfirmPassword(value);
        return;
      }

      if (name === 'reference' || name === 'complement' || name === 'number') {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [name]: value,
          },
        }));
      } else if (name === 'cep') {
        // Invalidate resolved address whenever CEP changes
        setFormData(prev => ({
          ...prev,
          cep: value,
          address: {
            street: '',
            neighborhood: '',
            city: '',
            zip: '',
            number: prev.address.number,
            complement: prev.address.complement,
            reference: prev.address.reference,
            recipient: '',
            uf: '',
          },
        }));
        setErrors(prev => ({ ...prev, cep: '', address: '' }));
      } else {
        // For other fields, update directly in formData
        setFormData(prev => ({
          ...prev,
          [name]: value,
        }));
      }
    },
    [],
  );

  const handleSelectChange = React.useCallback(
    (e: SelectChangeEvent<string>) => {
      const { name, value } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  // Handle CEP blur to fetch address
  const handleCepBlur = React.useCallback(async () => {
    const cepDigits = formData.cep.replace(/\D/g, '');

    if (!cepDigits) {
      setErrors(prev => ({
        ...prev,
        cep: 'CEP é obrigatório.',
      }));
      return;
    }

    if (cepDigits.length !== 8) {
      setErrors(prev => ({
        ...prev,
        cep: 'Digite um CEP válido (8 dígitos).',
      }));
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street: '',
          neighborhood: '',
          city: '',
          zip: '',
          recipient: '',
          uf: '',
        },
      }));
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `https://viacep.com.br/ws/${cepDigits}/json/`,
      );
      if (response.data.erro || !response.data.localidade) {
        setErrors(prev => ({
          ...prev,
          cep: 'CEP não encontrado.',
        }));
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            street: '',
            neighborhood: '',
            city: '',
            zip: '',
            recipient: '',
            uf: '',
          },
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          address: {
            street: response.data.logradouro || '',
            neighborhood: response.data.bairro || '',
            city: response.data.localidade,
            zip: cepDigits,
            number: prev.address.number,
            complement: prev.address.complement,
            reference: prev.address.reference,
            recipient: prev.fullName,
            uf: response.data.uf || '',
          },
        }));
        setErrors(prev => ({
          ...prev,
          cep: '',
          address: '',
          number: '',
        }));
      }
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        cep: 'Erro ao buscar o CEP. Tente novamente.',
      }));
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street: '',
          neighborhood: '',
          city: '',
          zip: '',
          recipient: '',
          uf: '',
        },
      }));
    } finally {
      setLoading(false);
    }
  }, [formData.cep]);

  // Convert birth date to Firebase format
  // const getFormattedBirthDate = () => {
  //   const { day, month, year } = formData;

  //   console.log("day:", day, typeof day);
  //   console.log("month:", month, typeof month);
  //   console.log("year:", year, typeof year);

  //   if (!day || !month || !year) return null;

  //   return new Date(
  //     `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  //   );
  // };

  // Handle form submission
  const handleSubmit = React.useCallback(async () => {
    const newErrors: Record<string, string> = {};

    if (!validateCPF(formData.cpf)) {
      newErrors.cpf = 'CPF inválido';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    Object.keys(formData).forEach(key => {
      const value = formData[key as keyof typeof formData];
      if (
        key !== 'complement' &&
        key !== 'reference' &&
        key !== 'id' &&
        key !== 'phoneNumber' &&
        key !== 'areaCode' &&
        key !== 'receiveUpdates' &&
        key !== 'address' &&
        (!value || (typeof value === 'string' && value.trim() === ''))
      ) {
        newErrors[key] = 'Campo obrigatório';
      }
    });

    const cepDigits = formData.cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) {
      newErrors.cep = 'Digite um CEP válido (8 dígitos).';
    } else if (!formData.address.city || !formData.address.uf) {
      newErrors.cep =
        'CEP inválido ou não encontrado. Busque um CEP válido antes de continuar.';
      newErrors.address = 'Endereço obrigatório. Informe um CEP válido.';
    }

    if (!formData.address.number?.trim()) {
      newErrors.number = 'Campo obrigatório';
    }

    // Check password match
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'As senhas não coincidem';
    }
    if (!phoneNumber) {
      newErrors.phoneNumber = 'Campo obrigatório';
    } else if (phoneNumber.length !== 9) {
      newErrors.phoneNumber = 'Número deve ter exatamente 9 dígitos';
    }
    if (!areaCode) {
      newErrors.areaCode = 'Campo obrigatório';
    } else if (areaCode.length !== 2) {
      newErrors.areaCode = 'DDD deve ter exatamente 2 dígitos';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        // Firebase Auth User Creation
        let res;
        try {
          res = await signupWithEmailAndPassword(formData.email, password);
        } catch (error: any) {
          if (error.code === 'auth/email-already-in-use') {
            setModalMessage('Este e-mail já está em uso.');
            setErrors(prev => ({
              ...prev,
              email: 'Este e-mail já está em uso.',
            }));
          } else if (error.code === 'auth/weak-password') {
            setModalMessage('A senha deve ter pelo menos 6 caracteres.');
            setErrors(prev => ({
              ...prev,
              password: ' ',
              confirmPassword: 'A senha deve ter pelo menos 6 caracteres.',
            }));
          } else if (error.code === 'auth/invalid-email') {
            setModalMessage('O e-mail fornecido não é válido.');
            setErrors(prev => ({
              ...prev,
              email: 'O e-mail fornecido não é válido.',
            }));
          } else {
            setModalMessage('Erro ao criar usuário. Tente novamente.');
          }
          setIsModalOpen(true);
          return;
        }

        // Create Customer in Payment Service
        // const customerData = {
        //   cpf: formData.cpf,
        //   email: formData.email,
        //   name: formData.fullName,
        //   phone: phoneNumber,
        //   areaCode: areaCode,
        // };

        // const resp = await createCustomer(customerData);

        if ('user' in res) {
          const userDoc = {
            ...formData,
            id: res.user.uid,
            phoneNumber: `(${areaCode}) ${phoneNumber}`,
            bornDate: birthDate ? birthDate.toDate() : new Date(),
            createdAt: new Date(), // Adding creation timestamp
            subscription: [],
          };

          // if (resp && resp.statusCode === 200) {
          //   userDoc.subscription = {
          //     renew: true,
          //     status: true,
          //     subscriptionCreatedAt: resp.subscriptionCreatedAt
          //       ? new Date(resp.subscriptionCreatedAt)
          //       : undefined,
          //     userID: resp.userID || '',
          //   };
          // }

          await UpdateHouse2Firebase(
            'subscription_users',
            userDoc,
            res.user.uid,
          );

          // Sign out the user after signup to ensure clean login flow
          await auth.signOut();

          navigate('/login', { state: location.state });
        } else {
          setErrors(prev => ({
            ...prev,
            firebase: 'Error on user creation',
          }));
          console.error('Error on user creation:', res);
        }
      } catch (error) {
        console.error('Erro ao registrar usuário:', error);
      }
    } else {
    }
  }, [
    formData,
    password,
    confirmPassword,
    birthDate,
    areaCode,
    phoneNumber,
    navigate,
  ]);

  return (
    <Box>
      {/* Main Container */}
      <ErrorModal
        open={isModalOpen}
        message={modalMessage}
        onClose={() => setIsModalOpen(false)}
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Grid
          container
          spacing={4}
          direction="column"
          alignItems="center"
          justifyContent="center"
        >
          {/* Personal Information Card */}
          <Grid item xs={12} sm={8} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
                >
                  Cadastro Dados Pessoais
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="E-mail"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={!!errors.email}
                      helperText={errors.email}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="CPF"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      error={!!errors.cpf}
                      helperText={errors.cpf}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nome Completo"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                    />
                  </Grid>
                  {/* Gender Selection */}
                  <Grid item xs={12}>
                    <FormControl fullWidth error={!!errors.gender}>
                      <InputLabel>Gênero</InputLabel>
                      <Select
                        name="gender"
                        value={formData.gender}
                        onChange={handleSelectChange}
                      >
                        <MenuItem value="male">Masculino</MenuItem>
                        <MenuItem value="female">Feminino</MenuItem>
                        <MenuItem value="prefer_not_to_say">
                          Prefiro não dizer
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Data de Nascimento
                    </Typography>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="pt-br"
                    >
                      <DatePicker
                        label="Data de Nascimento"
                        value={birthDate}
                        onChange={handleBirthDateChange}
                        format="DD/MM/YYYY"
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </LocalizationProvider>
                    {/* <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <FormControl fullWidth error={!!errors.day}>
                          <InputLabel>Dia</InputLabel>
                          <Select
                            name="day"
                            value={formData.day}
                            onChange={handleSelectChange}
                          >
                            {Array.from({ length: 31 }, (_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth error={!!errors.month}>
                          <InputLabel>Mês</InputLabel>
                          <Select
                            name="month"
                            value={formData.month}
                            onChange={handleSelectChange}
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>
                                {i + 1}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth error={!!errors.year}>
                          <InputLabel>Ano</InputLabel>
                          <Select
                            name="year"
                            value={formData.year}
                            onChange={handleSelectChange}
                          >
                            {Array.from({ length: 100 }, (_, i) => (
                              <MenuItem
                                key={currentYear - i}
                                value={currentYear - i}
                              >
                                {currentYear - i}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid> */}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Senha"
                      type="password"
                      name="password"
                      value={password}
                      onChange={handleInputChange}
                      error={!!errors.password}
                      helperText={errors.password}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirme Senha"
                      type="password"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={handleInputChange}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Telefone Celular
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="DDD"
                          name="areaCode"
                          value={areaCode}
                          onChange={handleInputChange}
                          error={!!errors.areaCode}
                          helperText={errors.areaCode}
                          inputProps={{
                            maxLength: 2,
                            pattern: '[0-9]*',
                            inputMode: 'numeric',
                          }}
                          placeholder="11"
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label="Número"
                          name="phoneNumber"
                          value={phoneNumber}
                          onChange={handleInputChange}
                          error={!!errors.phoneNumber}
                          helperText={errors.phoneNumber}
                          inputProps={{
                            maxLength: 9,
                            pattern: '[0-9]*',
                            inputMode: 'numeric',
                          }}
                          placeholder="999999999"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information Card */}
          <Grid item xs={12} sm={8} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}
                >
                  Cadastro de Endereço
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="CEP"
                      name="cep"
                      value={formData.cep}
                      onChange={handleInputChange}
                      onBlur={handleCepBlur}
                      error={!!errors.cep}
                      helperText={errors.cep}
                      InputProps={{
                        endAdornment: loading && <CircularProgress size={20} />,
                      }}
                    />
                  </Grid>
                  {formData.address && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Endereço"
                          name="address"
                          value={
                            formData.address.street
                              ? `${formData.address.street}, ${formData.address.neighborhood}, ${formData.address.city}`
                              : formData.address.city
                                ? `${formData.address.city} - ${formData.address.uf}`
                                : ''
                          }
                          onChange={handleInputChange}
                          disabled
                          error={!!errors.address}
                          helperText={errors.address}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Número"
                          name="number"
                          value={formData.address.number}
                          onChange={handleInputChange}
                          error={!!errors.number}
                          helperText={errors.number}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Complemento"
                          name="complement"
                          // value={formData.address}
                          onChange={handleInputChange}
                          error={!!errors.complement}
                          helperText={errors.complement}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Referência"
                          name="reference"
                          // value={formData.address.reference}
                          onChange={handleInputChange}
                          error={!!errors.reference}
                          helperText={errors.reference}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.receiveUpdates}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              receiveUpdates: e.target.checked,
                            }))
                          }
                        />
                      }
                      label="Quero receber novidades do Clube da 42 por e-mail, SMS e Whatsapp"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center' }}>
              <Button variant="contained" size="large" onClick={handleSubmit}>
                Cadastrar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SignUp;
